import { CustomLink } from '@/components/custom-link/CustomLink';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { GlobalClassName } from '@/shared/constant/GlobalClassName';
import type { SidebarTreeApiFolderNode } from '@/shared/constant/sidebar-tree/SidebarTreeNode';
import {
  DIRECTORY_TREE_DISPLAY_STYLE,
  FOLDER_CONTENTS_DISPLAYING_SETTINGS,
} from '@tarslib/types/lib/httpFolder';
import { FoxIconDownMd, FoxIconRightMd } from 'aui-biz-icons';
import clsx from 'clsx';
import React from 'react';
import styles from '../../SidebarTree.module.css';
import { useSidebarTreeStore } from '../../store/sidebarTreeStore';
import { generateClassNameBySidebarTreeNodeKey } from '../../util/generateClassNameBySidebarTreeNodeKey';
import { renderIcon } from '../../util/renderIcon';
import { SidebarModuleFolder } from '../sidebar-module-folder/SidebarModuleFolder';
import { SidebarTreeList } from '../sidebar-tree-list/SidebarTreeList';

export type SidebarFolderProps = {
  value: SidebarTreeApiFolderNode;
  indentation: number;
  index: number;
  className?: string;
  noLink?: boolean;
  prefixIcon?: React.ReactNode;
  suffixIcon?: React.ReactNode;
  firstLevelSelectedApiFolderId?: number;
  isHidden: boolean;
  displayName?: string;
};

export const SidebarFolder = React.memo(function SidebarFolderComponent(props: SidebarFolderProps) {
  const {
    value,
    indentation,
    index,
    className,
    noLink,
    firstLevelSelectedApiFolderId,
    isHidden,
    displayName,
  } = props;
  const { to, name, children, shareSettings, expanded: expandedValue } = value;

  const selected = useSidebarTreeStore((x) => x.selectedKey === value.key);
  const setSelectedKey = useSidebarTreeStore((x) => x.setSelectedKey);
  const parentSidebarTreeApiFolderNodes = useDocsDataModelStore(
    (x) => x.sidebarTree.parentSidebarTreeApiFolderNodes,
  );

  const [expanded, setExpanded] = React.useState(expandedValue);
  const { directoryTreeDisplayStyle, folderContentsDisplayingSettings } = shareSettings;

  React.useEffect(() => {
    if (parentSidebarTreeApiFolderNodes.some((x) => x.key === value.key)) {
      setExpanded(true);
    }
  }, [parentSidebarTreeApiFolderNodes, value.key]);

  const isGeneralFolderCannotClick = React.useMemo(() => {
    if (noLink) {
      return true;
    }

    if (directoryTreeDisplayStyle === DIRECTORY_TREE_DISPLAY_STYLE.GENERAL_FOLDER) {
      if (folderContentsDisplayingSettings === FOLDER_CONTENTS_DISPLAYING_SETTINGS.HIDE || !to) {
        return true;
      }
    }

    return false;
  }, [directoryTreeDisplayStyle, folderContentsDisplayingSettings, to, noLink]);

  if (directoryTreeDisplayStyle === DIRECTORY_TREE_DISPLAY_STYLE.MODULE_TITLE) {
    return (
      <SidebarModuleFolder
        value={value}
        indentation={indentation}
        index={index}
        isHidden={isHidden}
      />
    );
  }
  const prefixIcon = renderIcon(props.prefixIcon);
  const suffixIcon = renderIcon(props.suffixIcon);

  const isBold = selected || (value.id === firstLevelSelectedApiFolderId && indentation === 0);
  const isFirstLevelFolder = indentation === 0;
  const isNotNeedShowLink = isGeneralFolderCannotClick && !isHidden;
  const title = displayName || name;

  return (
    <li
      className={clsx(GlobalClassName.SidebarItemCategory, {
        [GlobalClassName.SidebarItemLink]: !isNotNeedShowLink,
      })}
    >
      {React.createElement(
        isNotNeedShowLink ? 'div' : CustomLink, // <CustomLink 搜索锚点
        (() => {
          const result = {
            to: to,
            className: clsx(
              styles['sidebar-tree-node'],
              isGeneralFolderCannotClick && 'cursor-pointer select-none',
              {
                [`${styles['sidebar-tree-node--selected']}`]: selected,
                ['font-600']: isBold,
                ['text-color']: isFirstLevelFolder,
                [generateClassNameBySidebarTreeNodeKey(value.key)]: selected,
              },
              className,
            ),
            title: title,
            onClick: () => {
              setExpanded(!expanded);
              if (!isGeneralFolderCannotClick) {
                setSelectedKey(value.key);
              }
            },
          };
          if (!isNotNeedShowLink) {
            return {
              ...result,
              isSampleLink: isHidden,
              sampleName: title,
            };
          }

          return result;
        })(),
        <>
          {prefixIcon}

          <span
            className={clsx('break-word', {
              'ml-2': prefixIcon !== undefined,
              'mr-2': suffixIcon !== undefined,
            })}
          >
            {title}
          </span>

          {suffixIcon}

          <div className="flex-1" />

          {directoryTreeDisplayStyle !== DIRECTORY_TREE_DISPLAY_STYLE.GO_TO_THE_SUBFOLDER && (
            <div
              className="flex h-[22px] w-[22px] items-center justify-center"
              onClick={(e) => {
                setExpanded(!expanded);
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {expanded ? (
                <FoxIconDownMd className="text-disabled" size={16} />
              ) : (
                <FoxIconRightMd className="text-disabled" size={16} />
              )}
            </div>
          )}
        </>,
      )}

      {directoryTreeDisplayStyle !== DIRECTORY_TREE_DISPLAY_STYLE.GO_TO_THE_SUBFOLDER && (
        <SidebarTreeList
          list={children}
          className={clsx({
            ['hidden']: !expanded,
            ['ml-3 border-l border-color-split pl-2']: expanded && !isHidden,
          })}
          indentation={indentation + 1}
          isHidden={isHidden || !expanded}
        />
      )}
    </li>
  );
});
